import { Answers, Condition, Config, Drug, Step, StepId } from "./types";

// check if the answer matches the condition
export function matches(condition: Condition, answer: any): boolean {
	if ("equals" in condition) {
		return answer === condition.equals;
	}

	if ("notEquals" in condition) {
		return answer !== condition.notEquals;
	}

	if ("in" in condition) {
		return condition.in.includes(answer);
	}

	if ("gt" in condition) {
		return typeof answer === "number" && answer > condition.gt;
	}

	if ("lt" in condition) {
		return typeof answer === "number" && answer < condition.lt;
	}

	return false;
}

export function resolveNextStep(step: Step, answer: any): StepId | null {
	if (step.next) {
		for (const rule of step.next) {
			if (matches(rule.when, answer)) return rule.goTo;
		}
	}
	return step.defaultNext ?? null;
}

export function buildActivePath(startId: string, answers: Record<string, any>, steps: Step[]) {
	const path: string[] = [];
	const seen = new Set<string>();
	const stepsById = steps.reduce((acc, step) => {
		acc[step.id] = step;
		return acc;
	}, {} as Record<string, Step>);

	let id: string | null = startId;

	while (id && !seen.has(id)) {
		seen.add(id);
		path.push(id);

		const step = stepsById[id];
		const answer = answers[id];

		// stop at the first unanswered step
		if (answer === undefined) break;

		id = resolveNextStep(step, answer);
	}

	return path;
}

export const calculateResults = (answers: Answers, config: Config, drugs: Drug[]) => {
	let recommendations: string[] = [];
	const treatmentOptions: {
		type: string;
		drugs: {
			drugId: string;
			dosage: string;
			frequency: string;
			duration: string;
			comment: string;
			howToGive: string;
		}[];
	}[] = [];

	// Get sensitivities from the unified sensitivities step
	const sens = answers.sensitivities as {
		kidneyIssue: boolean;
		pregnant: boolean;
		trimester: "trimester 1" | "trimester 2" | "trimester 3" | null;
		nursing: boolean;
		hasAllergies: boolean;
		selectedAllergies: string[];
	} | undefined;

	// Get blacklisted drugs based on pregnancy/trimester/nursing status
	let blacklistedDrugs: string[] = [];
	if (sens?.pregnant && sens.trimester && config?.blacklists) {
		blacklistedDrugs = [...blacklistedDrugs, ...(config.blacklists[sens.trimester] || [])];
	}
	if (sens?.nursing && config?.blacklists) {
		blacklistedDrugs = [...blacklistedDrugs, ...(config.blacklists.nursing || [])];
	}
	blacklistedDrugs = Array.from(new Set(blacklistedDrugs));

	// Get user allergies
	const userAllergies: string[] = sens?.hasAllergies ? (sens.selectedAllergies ?? []) : [];

	let protocolComment: string | undefined;
	let diagnosisComment: string | undefined;
	let diagnosisRecommendations: string[] = [];
	let diagnosisSpecialCases: string[] = [];

	// Get treatment from diagnosis
	if (answers.diagnosis && answers.protocol) {
		const protocol = config?.protocols?.find((p: any) => String(p.id) === String(answers.protocol));
		if (protocol && protocol.diagnoses) {
			const diagnosis = protocol.diagnoses.find((d: any) => String(d.id) === String(answers.diagnosis));
			if (diagnosis) {
				if (diagnosis.recommendations && diagnosis.recommendations.length > 0) {
					diagnosisRecommendations = diagnosis.recommendations;
				}
				if (diagnosis.specialCases && diagnosis.specialCases.length > 0) {
					diagnosisSpecialCases = diagnosis.specialCases;
				}
				if (diagnosis.comment) {
					diagnosisComment = diagnosis.comment;
				}

				// Get treatment options
				if (diagnosis.treatment) {
					// Filter first line options
					if (diagnosis.treatment.firstLine?.options) {
						treatmentOptions.push(
							...diagnosis.treatment.firstLine.options.map((opt: any) => ({
								...opt,
								type: "firstLine",
							}))
						);
					}

					// Filter alternative options
					if (diagnosis.treatment.alternative?.options) {
						treatmentOptions.push(
							...diagnosis.treatment.alternative.options.map((opt: any) => ({
								...opt,
								type: "alternative",
							}))
						);
					}
				}

			}
		}

		if (protocol?.comment) {
			protocolComment = protocol.comment;
		}
	}

	const trimesterLabels: Record<string, string> = {
		"trimester 1": "שליש ראשון",
		"trimester 2": "שליש שני",
		"trimester 3": "שליש שלישי",
	};

	const getInvalidReason = (drugId: string, isBlacklisted: boolean, isAllergic: boolean) => {
		if (isBlacklisted) {
			const reasons = [];
			const inPregnancy = sens?.pregnant && sens.trimester && config?.blacklists?.[sens.trimester as keyof typeof config.blacklists]?.includes(drugId);
			const inNursing = sens?.nursing && config?.blacklists?.nursing?.includes(drugId);
			
			if (inPregnancy) reasons.push(`לא מומלצת בהריון (${trimesterLabels[sens.trimester as string] || sens.trimester})`);
			if (inNursing) reasons.push("לא מומלצת בהנקה");
			
			if (reasons.length > 0) return reasons.join(" וגם ");
			return "תרופה ברשימה האסורה";
		}
		if (isAllergic) return "אלרגיה ידועה";
		return "";
	};

	const allTreatmentOptionsWithStatus = treatmentOptions.map((option) => {
		// Check each drug and mark why it's invalid
		const drugsWithStatus = option.drugs.map((drug: any) => {
			const isBlacklisted = blacklistedDrugs.includes(drug.drugId);
			const isAllergic = userAllergies.includes(drug.drugId);
			const fullReason = getInvalidReason(drug.drugId, isBlacklisted, isAllergic);
			return {
				...drug,
				isValid: !isBlacklisted && !isAllergic,
				isBlacklisted,
				isAllergic,
				reason: isBlacklisted ? "blacklisted" : isAllergic ? "allergic" : null,
				fullReason,
			};
		});

		// Check if ALL drugs in this option are valid
		const allDrugsValid = drugsWithStatus.every((drug: any) => drug.isValid);

		// Find which drugs are invalid with reasons
		const invalidDrugs = drugsWithStatus.filter((drug: any) => !drug.isValid);

		return {
			...option,
			isAllowed: allDrugsValid,
			invalidDrugs: invalidDrugs,
			drugsWithStatus: drugsWithStatus,
		};
	});

	// Separate by type (firstLine vs alternative) and then by status (allowed first)
	const firstLineOptions = allTreatmentOptionsWithStatus
		.filter((opt) => opt.type === "firstLine")
		.sort((a, b) => (a.isAllowed === b.isAllowed ? 0 : a.isAllowed ? -1 : 1));

	const alternativeOptions = allTreatmentOptionsWithStatus
		.filter((opt) => opt.type === "alternative")
		.sort((a, b) => (a.isAllowed === b.isAllowed ? 0 : a.isAllowed ? -1 : 1));

	// Add warnings for blacklisted/allergic drugs
	if (blacklistedDrugs.length > 0) {
		const blacklistedNames = blacklistedDrugs.map((drugId) => {
			const drug = drugs.find((d: any) => d.id === drugId);
			return drug?.displayName || drugId;
		});
		recommendations.push(`⚠️ תרופות לא מומלצות: ${blacklistedNames.join(", ")}`);
	}

	if (userAllergies.length > 0) {
		const allergicNames = userAllergies.map((drugId) => {
			const drug = drugs.find((d: any) => d.id === drugId);
			return drug?.displayName || drugId;
		});
		recommendations.push(`⚠️ אלרגיות: ${allergicNames.join(", ")}`);
	}

	// Collect all removed drugs with their reasons
	const removedDrugsMap = new Map<string, { reason: string; reasonType: string }>();

	// Add blacklisted drugs
	blacklistedDrugs.forEach((drugId) => {
		const reasons = [];
		const inPregnancy = sens?.pregnant && sens.trimester && config?.blacklists?.[sens.trimester as keyof typeof config.blacklists]?.includes(drugId);
		const inNursing = sens?.nursing && config?.blacklists?.nursing?.includes(drugId);
		
		if (inPregnancy) reasons.push(`לא מומלצת בהריון (${trimesterLabels[sens.trimester as string] || sens.trimester})`);
		if (inNursing) reasons.push("לא מומלצת בהנקה");
		
		const reasonText = reasons.length > 0 ? reasons.join(" וגם ") : "תרופה ברשימה האסורה";
		removedDrugsMap.set(drugId, { reason: reasonText, reasonType: "blacklisted" });
	});

	// Add allergic drugs
	userAllergies.forEach((drugId) => {
		removedDrugsMap.set(drugId, { reason: "אלרגיה ידועה", reasonType: "allergic" });
	});

	// Add warning if options were removed
	const allRemovedOptions = [...firstLineOptions, ...alternativeOptions].filter(
		(opt) => !opt.isAllowed
	);
	if (allRemovedOptions.length > 0) {
		const removedDrugNames = new Set<string>();
		allRemovedOptions.forEach((option) => {
			option.drugs.forEach((drug: any) => {
				const isBlacklisted = blacklistedDrugs.includes(drug.drugId);
				const isAllergic = userAllergies.includes(drug.drugId);
				if (isBlacklisted || isAllergic) {
					const drugInfo = drugs.find((d: any) => d.id === drug.drugId);
					removedDrugNames.add(drugInfo?.displayName || drug.drugId);
				}
			});
		});
		if (removedDrugNames.size > 0) {
			recommendations.push(
				`⚠️ אפשרויות טיפול הוסרו בגלל תרופות לא מתאימות: ${Array.from(removedDrugNames).join(
					", "
				)}`
			);
		}
	}

	// Convert removed drugs map to array
	const removedDrugsList = Array.from(removedDrugsMap.entries()).map(([drugId, info]) => {
		const drugInfo = drugs.find((d: any) => d.id === drugId);
		return {
			drugId,
			drugName: drugInfo?.displayName || drugId,
			reason: info.reason,
			reasonType: info.reasonType,
		};
	});

	return {
		recommendations,
		firstLineOptions,
		alternativeOptions,
		answers,
		removedDrugs: removedDrugsList,
		protocolComment,
		diagnosisComment,
		diagnosisRecommendations,
		diagnosisSpecialCases,
	};
};

type Answers = Record<string, any>;

export function getTotalSteps(startId: string, steps: Step[], answers: Answers): number {
	let total = 0;
	let id: string | null = startId;
	const seen = new Set<string>();

	const stepsById = steps.reduce((acc, step) => {
		acc[step.id] = step;
		return acc;
	}, {} as Record<string, Step>);

	while (id && !seen.has(id)) {
		console.log("id", id);
		seen.add(id);
		total++;

		const step = stepsById[id];
		const answer = answers[id];

		id = resolveNextStep(step, answer);
		console.log("i2d", id);
	}

	return total;
}
